/**
 * Editing what an asset means, never what it is.
 *
 * Assets are immutable — the bytes, the dimensions and the codec are fixed at
 * upload and a replacement is a new asset with a new id, because devices cache
 * by immutable key. So the only editable fields are the three a person changes
 * their mind about: what it is called, which brands may use it, and when it
 * comes off the wall.
 */

import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { errorMessage } from '../../lib/api.js';
import { formatBytes } from '../../lib/format.js';
import type { MediaAsset, MediaUpdate } from '../../lib/media.js';
import {
  formatBrands,
  formatDimensions,
  formatDuration,
  fromExpiryInputValue,
  parseBrands,
  toExpiryInputValue,
  typeLabel,
} from './presentation.js';

export interface MediaEditFormProps {
  asset: MediaAsset;
  busy: boolean;
  error: unknown;
  onSubmit: (patch: MediaUpdate) => void;
  onCancel: () => void;
}

export function MediaEditForm({
  asset,
  busy,
  error,
  onSubmit,
  onCancel,
}: MediaEditFormProps): ReactElement {
  const [title, setTitle] = useState(asset.title);
  const [brands, setBrands] = useState(() => formatBrands(asset.brands));
  const [expiry, setExpiry] = useState(() => toExpiryInputValue(asset.contentExpiresAt));

  const shared = asset.storeId === null;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (busy) return;

    const patch: MediaUpdate = {
      title: title.trim().slice(0, 200),
      contentExpiresAt: fromExpiryInputValue(expiry),
    };
    // Brands only mean anything on a shared asset; sending them for a store's
    // own file would write a filter nothing reads.
    if (shared) patch.brands = parseBrands(brands);

    onSubmit(patch);
  };

  return (
    <form className="form" onSubmit={submit}>
      <p className="form__context">
        {typeLabel(asset.type)} · {formatDimensions(asset.width, asset.height)} ·{' '}
        {formatBytes(asset.bytes)}
        {asset.durationMs === null ? '' : ` · ${formatDuration(asset.durationMs)}`}
      </p>

      <div className="form__grid">
        <label className="field field--span">
          <span className="field__label">Title</span>
          <input
            className="input"
            type="text"
            value={title}
            maxLength={200}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
          />
          <span className="field__hint">
            What this shows up as in the playlist editor. The file name is kept either way.
          </span>
        </label>

        {shared ? (
          <label className="field field--span">
            <span className="field__label">Brands</span>
            <input
              className="input"
              type="text"
              value={brands}
              placeholder="Pizza Bros, Rosetown Kebab"
              onChange={(event) => setBrands(event.target.value)}
            />
            <span className="field__hint">
              Comma separated. Empty means every brand in the shared library can use it.
            </span>
          </label>
        ) : null}

        <label className="field field--span">
          <span className="field__label">Plays until</span>
          <input
            className="input"
            type="date"
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
          />
          <span className="field__hint">
            Runs through the whole of that day and stops at midnight. Screens enforce it themselves,
            so it still happens on a TV that has been off the internet for a week. Leave it empty for
            no expiry.
          </span>
        </label>
      </div>

      {error !== null && error !== undefined ? (
        <p className="alert alert--danger" role="alert">
          {errorMessage(error)}
        </p>
      ) : null}

      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
